import { IconCatalog, IconChart, IconFarm, IconHome, IconLineage, IconSettings, IconTrendingUp } from '../../common/icons';

// TSS vite/05 — nav catalog for the client portal. Single source of truth for
// the sidebar (desktop) and the mobile bottom-bar + hamburger drawer.
//
// Each main view declares the backing role (must be in the client's
// `roles_with_data` — contract rule "el front oculta las pestañas de rol sin
// datos") and whether its surface is live yet. `role: null` = role-agnostic,
// always visible (e.g. the home landing). A view backed by MORE THAN ONE role
// declares `roles: [...]` (any-of): visible if the client has at least one —
// p.ej. "Mis fincas" respalda receptoras (partner) y donadoras (donor_owner).
// `visibleNav()` filters by the live roles at render time. `end: true` marks a
// route that must match exactly for the active state (the "/" home would
// otherwise match every path).
//
// `label` = full name (sidebar + drawer); `short` = tight bottom-bar caption.
// `icon` is a currentColor SVG element; CSS sizes it per placement (medium in
// the bottom bar, small in the drawer, default in the sidebar).

export const MAIN_NAV = [
    // PRIMERO — el enfoque de la app: las INVERSIONES del cliente (donadoras
    // co-poseídas: valorización, liquidaciones, dividendo). Antes "Mis Vacas";
    // reencuadrado a "Inversiones" para que el cliente sienta que gana dinero.
    // (Ruta /mis-vacas por ahora; ícono de crecimiento.)
    {
        key: 'inversiones',
        label: 'Inversiones',
        short: 'Inversiones',
        to: '/mis-vacas',
        // role: null → SIEMPRE visible: es el enfoque de la app y sirve a los
        // DOS caminos. Inversionista (donor_owner con datos) → dashboard de su
        // inversión; no-inversionista → invitación a invertir. El branch lo hace
        // la página (mis-vacas.jsx) por roles_with_data.
        role: null,
        available: true,
        icon: <IconTrendingUp />,
    },
    {
        key: 'home',
        label: 'Inicio',
        short: 'Inicio',
        to: '/',
        role: null,
        available: true,
        end: true,
        icon: <IconHome />,
    },
    {
        key: 'informes',
        label: 'KPIs y estadísticas',
        short: 'KPIs',
        to: '/informes',
        roles: ['partner', 'donor_owner'],
        available: true,
        icon: <IconChart />,
    },
    {
        key: 'parcerias',
        label: 'Parcerías',
        short: 'Parcerías',
        to: '/parcerias',
        role: 'partner',
        available: true,
        icon: <IconLineage />,
    },
    {
        key: 'fincas',
        label: 'Mis fincas',
        short: 'Fincas',
        to: '/fincas',
        roles: ['partner', 'donor_owner'],
        available: true,
        icon: <IconFarm />,
    },
    {
        key: 'propuestas',
        label: 'Mis propuestas',
        short: 'Propuestas',
        to: '/mis-propuestas',
        roles: ['donor_owner'],
        available: true,
        icon: <IconCatalog />,
    },
];

// A view is visible when the surface is live AND its role gate passes:
//  · `roles: [...]` (any-of) → visible if the client has at least one.
//  · `role: 'x'`            → visible if the client has that role.
//  · `role: null`          → role-agnostic, always visible (e.g. the home).
// `roles` arg = user.roles_with_data (array). Mirrors the Fase-1 header rule.
function passesRole(item, set) {
    if (!item || !item.available) return false;
    if (Array.isArray(item.roles)) return item.roles.some((r) => set.has(r));
    return item.role == null || set.has(item.role);
}

export function visibleNav(roles) {
    const set = new Set(Array.isArray(roles) ? roles : []);
    return MAIN_NAV.filter((item) => passesRole(item, set));
}

// MOBILE bottom bar only — a curated PRIMARY set (the desktop sidebar and the
// mobile drawer keep the full MAIN_NAV via visibleNav). Order puts INVERSIONES
// in the MIDDLE (marked `center`) so it renders as the prominent raised center
// button — el enfoque de la app. "Ajustes" (/perfil) es mobile-only, no va en
// MAIN_NAV.
const AJUSTES_ITEM = {
    key: 'ajustes',
    label: 'Ajustes',
    short: 'Ajustes',
    to: '/perfil',
    role: null,
    available: true,
    icon: <IconSettings />,
};

export function bottomNav(roles) {
    const set = new Set(Array.isArray(roles) ? roles : []);
    // Orden pensado para que Inversiones quede al centro: Inicio · Fincas ·
    // [INVERSIONES] · Parcerías · Ajustes (los ausentes por rol se filtran).
    const items = ['home', 'fincas', 'inversiones', 'parcerias']
        .map((key) => MAIN_NAV.find((item) => item.key === key))
        .filter((item) => passesRole(item, set))
        .map((item) => (item.key === 'inversiones' ? { ...item, center: true } : item));
    return [...items, AJUSTES_ITEM];
}
